/**
 * Navigational rename commands (Phase-3 Slice 35).
 *
 * PRD: Ralph/NavigationalFeatures/PRD.md § Slice 35.
 *
 * Surfaces "Undo last rename" in the command palette. Pops the most-recent
 * entry from `useRenameUndoLog` for the active connection and runs the
 * stored `undoBatch` via `renameService.undoLastRename`. The command is
 * hidden when no connection is active or the undo log is empty for the
 * active connection — `isVisible` is recomputed each time the palette opens.
 */

import { Undo2 } from 'lucide-react';

import { toast } from '@/components/ui/toaster';

import { undoLastRename, useRenameUndoLog } from '../../navigational-features/rename';
import type { CommandDefinition } from '../types/commandTypes';
import { useCommandPaletteStore } from '../store/commandPaletteStore';
import { useDatabaseUIStore } from '../../database-objects/store/uiStore';
import { useConnectionStore } from '../../connections/store/connectionStore';

function closeAndRun(fn: () => void | Promise<void>) {
  useCommandPaletteStore.getState().close();
  setTimeout(fn, 0);
}

function getActiveConnectionAndDatabase(): {
  connectionId: string;
  database: string;
} | undefined {
  const valid = useDatabaseUIStore.getState().getValidSelectedDatabase();
  if (valid) return { connectionId: valid.connectionId, database: valid.database };

  // Fallback to the selected connection without a database — the executor
  // can still issue catalog-level ALTERs without a `USE`.
  const cid = useDatabaseUIStore.getState().selectedConnectionId;
  if (!cid) return undefined;
  const ac = useConnectionStore.getState().activeConnections
    .find((c) => c.id === cid);
  if (!ac || ac.status !== 'connected') return undefined;
  return { connectionId: cid, database: ac.connection?.database ?? '' };
}

export const navigationalRenameCommands: CommandDefinition[] = [
  {
    id: 'nav:undo-last-rename',
    label: 'Undo last rename',
    category: 'navigation',
    icon: Undo2,
    keywords: ['rename', 'undo', 'revert', 'rollback', 'navigational'],
    needsContext: 'connection',
    isVisible: () => {
      const target = getActiveConnectionAndDatabase();
      if (!target) return false;
      return useRenameUndoLog.getState().canUndo(target.connectionId);
    },
    execute: () => closeAndRun(async () => {
      const target = getActiveConnectionAndDatabase();
      if (!target) {
        toast.error({
          title: 'Undo last rename',
          description: 'No active connection.',
        });
        return;
      }
      if (!useRenameUndoLog.getState().canUndo(target.connectionId)) {
        toast.default({
          title: 'Undo last rename',
          description: 'Nothing to undo on this connection.',
        });
        return;
      }
      try {
        const outcome = await undoLastRename({
          connectionId: target.connectionId,
          database: target.database,
        });
        if (outcome.status === 'no-entry') {
          toast.default({
            title: 'Undo last rename',
            description: 'Nothing to undo on this connection.',
          });
          return;
        }
        const renameResult = outcome.result;
        if (renameResult.status === 'success') {
          const from = outcome.undoneBatch.target.toIdentifier.name;
          const to = outcome.undoneBatch.target.fromIdentifier.name;
          toast.success({
            title: 'Rename undone',
            description: `Reverted ${from} → ${to}.`,
          });
          return;
        }
        toast.error({
          title: 'Undo last rename failed',
          description: renameResult.errorMessage ?? `Status: ${renameResult.status}`,
        });
      } catch (err) {
        toast.error({
          title: 'Undo last rename failed',
          description: err instanceof Error ? err.message : String(err),
        });
      }
    }),
  },
];
